import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import ScreenWrapper from "crqlar-react-common/dist/components/ScreenWrapper";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    Stack,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import * as React from "react";
import {useMemo, useState} from "react";
import {
    AgeCategory
} from "crqlar-react-common/src/models/ageCategory/ageCategory.model";
import AvailabilitySection from "./AvailabilitySection";
import AcademyCourseSection from "./AcademyCourseSection";
import {showsAcademyCourse} from "./academyCourse.util";
import {academySector} from "../common/academyTags.util";
import {
    ServiceCategory
} from "crqlar-react-common/src/models/serviceCategory/serviceCategory.model";
import {
    useAllServiceCategories
} from "crqlar-react-common/dist/utils/hooks/services/useAllServiceCategories";
import {
    useAllAgeCategories
} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {
    useAllSectors
} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import ImageUploadField, {
    ALL_IMAGE_UPLOAD_RATIOS
} from "crqlar-react-common/dist/components/ImageUploadField";
import {
    useAllResourceCategories
} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import {
    useInstanceTasks
} from "crqlar-react-common/dist/utils/hooks/instanceTasks/useInstanceTask";
import {
    InstanceAdditionalDataKey
} from "crqlar-react-common/dist/models/instanceTasks/instanceTask.model";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ResourceInlineEditDialog from "./ResourceInlineEditDialog";
import {
    useAllServices
} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {
    useAllRootResources
} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {
    useAvailability,
    useAvailabilityExceptions
} from "crqlar-react-common/dist/utils/hooks/availability/useResourceAvailability";
import {useTranslation} from "react-i18next";
import CountTextField from "crqlar-react-common/dist/components/CountTextField";
import AdditionalProperties
    from "crqlar-react-common/dist/components/AdditionalProperties";
import LocalizedTextField
    from "crqlar-react-common/dist/components/LocalizedTextField";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import Property from "crqlar-react-common/dist/components/Property";
import NewColorPicker from "crqlar-react-common/dist/components/NewColorPicker";
import NewStatusPicker from "crqlar-react-common/dist/components/NewStatusPicker";
import ExternalInfoPreview from "../common/ExternalInfoPreview";
import {
    ResourceCategory
} from "crqlar-react-common/dist/models/resourceCategory/resourceCategory.model";
import {AgendaV3} from "crqlar-react-common/dist/models/agenda/agenda.model";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import {useHasGuestAppModule} from "crqlar-react-common/dist/utils/hooks/guestApp/useGuestAppModule";
import {ServiceStatus} from "crqlar-react-common/dist/models/service/serviceStatus.model";
import {getContrastingColor} from "crqlar-react-common/dist/utils/color.util";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {Page} from "crqlar-react-common/dist/models/common/page.model";
import {useMutation, useQueryClient} from "react-query";
import {useSnackbar} from "notistack";
import {LoadingButton} from "@mui/lab";
import MuiLink from "@mui/material/Link";
import {Link as RouterLink} from "react-router-dom";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import {canBeTipService, isStaffCategory, tipServiceConflict} from "./tipService.util";

interface Props {
    serviceData: UseEntityData<Service>
    updateField: (field: keyof Service, value: any) => void
    showBufferTimeStatus?: boolean
    agendas: AgendaV3[]
    selectedAgendas: AgendaV3[]
    updateAgendas: (agendas: AgendaV3[]) => void
    section?: 'basic' | 'content'
    /** Why the backend refused the last save of this tip service, until the service changes. */
    tipServiceRefusal?: string
}

const ServiceDetailsForm = (props: Props) => {

    // Vars and constants
    const {t} = useTranslation()
    const queryClient = useQueryClient()
    const {enqueueSnackbar} = useSnackbar()
    const {hasGuestAppModule} = useHasGuestAppModule()
    const categories = useAllServiceCategories()
    const ageCategories = useAllAgeCategories()
    const resources = useAllRootResources()

    // Props
    const serviceData = props.serviceData
    const updateField = props.updateField
    const showBufferTimeStatus = props.showBufferTimeStatus

    const sectors = useAllSectors()

    const service = serviceData.item
    const disabled = !!service.managedExternally
    const nameMissing = !disabled && isRequiredTextMissing(serviceData, service.name)
    const restaurantSectorId = sectors.find(s => s.sectorType === SectorType.RESTAURANT)?.id

    const currentSector = sectors.find(s => s.id === service.sectorIds?.[0])
    const isWeeklyPlanner = currentSector?.sectorType === SectorType.WEEKLY_PLANNER
    const isMewsManaged = service.externalInfo?.some(e => e.provider === 'mews')

    const services = useAllServices()
    const currentDefaultService = services.find(s => s.id === currentSector?.defaultServiceId)
    const isDefaultService = !!service.id && currentSector?.defaultServiceId === service.id

    const allResourceCategories = useAllResourceCategories()

    // Category IDs collected from the resources currently linked to this service
    const linkedCategoryIds = useMemo(() => {
        const set = new Set<string>()
        service.resourceIds?.forEach(resourceId => {
            resources.find(r => r.id === resourceId)?.categoryIds?.forEach(cid => set.add(cid))
        })
        return set
    }, [service.resourceIds, resources])

    // Subset of allResourceCategories whose resources are linked to this service
    const linkedResourceCategories = useMemo(
        () => allResourceCategories.filter(rc => linkedCategoryIds.has(rc.id as string)),
        [allResourceCategories, linkedCategoryIds]
    )

    // Sector IDs from the selected service category (more complete than service.sectorIds which
    // only stores the first one). Falls back to service.sectorIds for backwards compatibility.
    const selectedServiceCategory = categories.find(c => c.id === service.categoryIds?.[0])
    const relevantSectorIds = selectedServiceCategory?.sectorIds ?? service.sectorIds ?? []

    // Courses live in the Akademie sector, which only Forsthofgut has.
    const showAcademyCourse = showsAcademyCourse(academySector(sectors), relevantSectorIds, service.tags)

    // Use relevantSectorIds so that isRestaurant is true as soon as the Restaurant service
    // category is selected, even before the service is saved (service.sectorIds may lag behind)
    const isRestaurant = !!(restaurantSectorId && relevantSectorIds.includes(restaurantSectorId))

    const matchingResourceCategoryIds = new Set(allResourceCategories
        .filter(rc => rc.sectorIds?.some(sectorId => relevantSectorIds.includes(sectorId)))
        .map(rc => rc.id as string))
    const sameSectorResourceIds = new Set(resources.filter(resource =>
        resource.sectorIds?.some(sectorId => relevantSectorIds.includes(sectorId)) ||
        resource.categoryIds?.some(categoryId => matchingResourceCategoryIds.has(categoryId))
    ).map(resource => resource.id))

    const sortedResources = useMemo(
        () => [...resources].sort((left, right) => (left.name ?? '').localeCompare(right.name ?? '')),
        [resources]
    )

    // We can have only one no show service per sector
    // So we need to find if there is another no show service in the same sector
    const otherNoShowServices = services.filter(s => s.noShowService && s.id !== service.id)
    const sectorsWithOtherNoShowServices = otherNoShowServices.map(s => s.sectorIds || []).flat()


    const instanceTaskQuery = useInstanceTasks(
        serviceData?.item.id
            ? new Map<InstanceAdditionalDataKey, unknown>([
                [InstanceAdditionalDataKey.SERVICE_ID, serviceData.item.id]
            ])
            : undefined
    )

    const instanceTask = instanceTaskQuery.data?.[0]


    const resourceIds = service.resourceIds ?? []
    const {data} = useAvailability(resourceIds)
    const availabilities = data ?? []

    const exceptionsQuery = useAvailabilityExceptions(availabilities.length > 0 ? availabilities.map(a => a.id!) : [])
    const exceptions = exceptionsQuery.data ?? []


    // Lists named availabilities grouped by each selected resource.
    // Format: ResourceName → [AvailabilityName (rrule summary)].

    type Resource = ReturnType<typeof useAllRootResources>[number]

    const [editingResource, setEditingResource] = useState<Resource | null>(null)

    const resourceById = useMemo(() => {
        const m = new Map<string, Resource>()
        for (const r of resources) m.set(r.id!, r)
        return m
    }, [resources])

    const mappedResources: Resource[] =
        service.resourceIds
            ?.map(id => resourceById.get(id))
            .filter((r): r is Resource => Boolean(r)) ?? []

    const serviceColor = service?.color?.background || CommonThemeColors.sys.success.background

    //endregion Sate

    //region Handlers

    const noShowAlreadyExistsForSector = () => {
        return service.sectorIds?.some(s => sectorsWithOtherNoShowServices.includes(s))
    }

    // A tip service takes the tips of staff only - its linked resources - by the backend's rule
    const staffCategoryIds = useMemo(
        () => new Set(allResourceCategories.filter(isStaffCategory).map(category => category.id as string)),
        [allResourceCategories]
    )
    const isStaff = (resourceId: string) => {
        const resource = resourceById.get(resourceId)
        return resource ? (resource.categoryIds ?? []).some(categoryId => staffCategoryIds.has(categoryId)) : undefined
    }
    const resourceNames = (ids: string[]) => ids.map(id => resourceById.get(id)?.name?.trim() || id).join(', ')

    // The tip services' rule, checked as the backend checks it and said in its words: not bookable, staff
    // only, one default tip service per sector and any number linked to staff - a therapist's own takes
    // the tips for their treatments
    const tipConflict = tipServiceConflict(service, services, isStaff)
    const tipConflictMessage = !tipConflict ? undefined
        : tipConflict.kind === 'BOOKABLE' ? t('A tip service can\'t be bookable: set a status other than Active, or turn off Tip service.')
        : tipConflict.kind === 'NOT_STAFF' ? t('A tip service takes the tips of staff only: {{names}} is not staff.', {
            count: tipConflict.resourceIds.length,
            names: resourceNames(tipConflict.resourceIds),
        })
        : tipConflict.kind === 'DEFAULT' ? t('"{{name}}" already is the default tip service of this sector. Pick staff for this one, or turn it off.', {
            name: tipConflict.other.name ?? '',
        })
        : t('{{names}} already has a tip service in this sector: "{{name}}".', {
            count: tipConflict.resourceIds.length,
            names: resourceNames(tipConflict.resourceIds),
            name: tipConflict.other.name ?? '',
        })
    const tipServiceWarning = tipConflictMessage ?? props.tipServiceRefusal

    const defaultServiceMutation = useMutation(
        async () => {
            if (!service.id || !currentSector?.id) throw new Error('Service and sector are required')

            return SectorService.updateSector({
                ...currentSector,
                defaultServiceId: service.id
            })
        },
        {
            onSuccess: (updatedSector) => {
                queryClient.setQueryData<Page<Sector> | undefined>('allSectors', current => {
                    if (!current?.content) return current

                    return {
                        ...current,
                        content: current.content.map(sector =>
                            sector.id === updatedSector.id ? updatedSector : sector
                        )
                    }
                })
                void queryClient.invalidateQueries(['allSingleSector', updatedSector.id])
                void queryClient.invalidateQueries(['useSingleSector', updatedSector.id])
                enqueueSnackbar(t('Default service updated'), {variant: 'success'})
            },
            onError: () => {
                enqueueSnackbar(t('Could not update the default service'), {variant: 'error'})
            }
        }
    )

    //endregion Handlers

    //region UI

    const sectionTitleSx = {
        fontSize: {xs: '18px', sm: '20px'},
        lineHeight: 1.25,
    }

    const fieldGroupGap = {xs: 1.5, sm: 1}

    const sectionDividerSx = {
        display: {xs: 'none', sm: 'block'},
    }

    // Showing the progress of the buffer time update task
    // When changing the resource block durations, a background task is triggered to update the buffer times of existing availabilities
    const bufferTimeStatusSection = (
        <>
            {
                showBufferTimeStatus &&
                <Alert severity={instanceTask?.status ? 'info' : 'success'}>
                    <Typography variant={'h6'}>
                        {t(`Buffer time update status: ${instanceTask?.status || 'COMPLETED'}`)}
                    </Typography>
                </Alert>
            }
        </>
    )

    const basicInfoSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Basic info')}
            </Typography>

            <Stack gap={fieldGroupGap}>
                <Property
                    title={t('Name')}
                    tooltip={t('Display label for this service in reservations, calendars, and booking surfaces.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={service.name || ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                        disabled={disabled}
                    />
                </Property>

                <Property
                    title={t('Color')}
                    tooltip={t('Color used to identify this service in calendar and reservation views.')}
                >
                    <NewColorPicker
                        value={serviceColor}
                        disabled={disabled}
                        isRequired
                        onChange={(newColor) => {
                            updateField('color', {
                                ...service?.color,
                                background: newColor,
                                text: getContrastingColor(newColor)
                            })
                        }}
                    />
                </Property>

                <Property
                    title={t('Status')}
                    tooltip={hasGuestAppModule
                        ? t('Only Active services are eligible for new guest bookings. Draft services remain available to staff but are hidden from guests. Active Not Bookable services can still appear in existing guest reservations but are excluded from new guest booking lists.')
                        : t('Active services are eligible for booking. Draft services remain available to staff but are excluded from guest booking flows.')}
                >
                    <NewStatusPicker<ServiceStatus>
                        value={service.status || ServiceStatus.DRAFT}
                        onChange={(newStatus) => updateField('status', newStatus)}
                        disabled={disabled}
                        ariaLabel={t('Status')}
                        options={[
                            {value: ServiceStatus.DRAFT, label: t('Draft')},
                            {value: ServiceStatus.ACTIVE, label: t('Active')},
                            ...(hasGuestAppModule
                                ? [{value: ServiceStatus.ACTIVE_NOT_BOOKABLE, label: t('Active Not Bookable')}]
                                : []),
                        ]}
                    />
                </Property>

                <Property
                    title={t('Order priority')}
                    tooltip={t('Controls app display order for this service; lower numbers appear first.')}
                >
                    <TextField
                        id={'order-priority'}
                        size={'small'}
                        value={service.orderPriority ?? ''}
                        onChange={(e) => updateField('orderPriority', e.target.value === '' ? undefined : parseInt(e.target.value))}
                        fullWidth
                        type={'number'}
                        disabled={disabled}
                        InputProps={{inputProps: {min: 0}}}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const linkedResourcesField = (
        <Property
            title={t('Linked resources')}
            tooltip={t('Resources that can be assigned to reservations for this service; changing them also cleans resource-specific timing rules.')}
            labelAlignment={'top'}
        >
            <Stack gap={1} width={'100%'}>
                <Autocomplete
                    id={'resources'}
                    multiple
                    options={sortedResources}
                    getOptionLabel={(option: any) => option.name || ''}
                    value={service.resourceIds?.map(id => resources.find(c => c.id === id)).filter(Boolean) as any[] || []}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    fullWidth
                    onChange={(e, value) => {
                        const newResourceIds = value.map((v: any) => v.id)
                        const newCategoryIds = new Set<string>()
                        newResourceIds.forEach((rid: string) => {
                            resources.find(r => r.id === rid)?.categoryIds?.forEach(cid => newCategoryIds.add(cid))
                        })
                        const cleanMap = (map?: Record<string, any>) => {
                            if (!map) return map
                            return Object.fromEntries(
                                Object.entries(map).filter(([k]) => k === '' || newCategoryIds.has(k))
                            )
                        }
                        serviceData.setItem({
                            ...service,
                            resourceIds: newResourceIds,
                            preparationTimeMinutes: cleanMap(service.preparationTimeMinutes),
                            resourceCategoryDurationMinutes: cleanMap(service.resourceCategoryDurationMinutes),
                            assignedResourcesRequirementPerCategory: cleanMap(service.assignedResourcesRequirementPerCategory)
                        })
                    }}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option: Resource, index: number) => {
                            const {key: _tagKey, ...chipProps} = getTagProps({index}) as any
                            return (
                                <Chip
                                    key={option.id ?? index}
                                    {...chipProps}
                                    label={option.name}
                                    deleteIcon={
                                        <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
                                            {!disabled && (
                                                <EditIcon
                                                    sx={{fontSize: 18, cursor: 'pointer'}}
                                                    onMouseDown={(e: React.MouseEvent) => {
                                                        e.stopPropagation()
                                                        e.preventDefault()
                                                    }}
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation()
                                                        setEditingResource(option)
                                                    }}
                                                />
                                            )}
                                            <CancelIcon sx={{fontSize: 20}}/>
                                        </span>
                                    }
                                    sx={{
                                        m: 0.5,
                                        height: 36,
                                        '& .MuiChip-label': {px: 1.5},
                                        '& .MuiChip-deleteIcon': {mr: 1, ml: 0.75}
                                    }}
                                />
                            )
                        })
                    }
                    renderOption={(props, option: Resource) => {
                        const editBtn = !disabled && (
                            <IconButton
                                size="small"
                                onMouseDown={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    setEditingResource(option)
                                }}
                                sx={{ml: 'auto', flexShrink: 0}}
                            >
                                <EditIcon fontSize="small"/>
                            </IconButton>
                        )

                        if (!relevantSectorIds.length) {
                            return (
                                <Box component="li" {...props} key={option.id}
                                     style={{
                                         display: 'flex',
                                         alignItems: 'center',
                                         justifyContent: 'space-between'
                                     }}>
                                    <span>{option.name}</span>
                                    {editBtn}
                                </Box>
                            )
                        }

                        const backgroundColor = sameSectorResourceIds.has(option.id)
                            ? CommonThemeColors.sys.success.light
                            : CommonThemeColors.sys.error.light

                        return (
                            <Box component="li" {...props} key={option.id}
                                 style={{
                                     display: 'flex',
                                     alignItems: 'center',
                                     justifyContent: 'space-between'
                                 }}>
                                <Chip
                                    label={option.name}
                                    style={{
                                        backgroundColor,
                                        color: 'black',
                                    }}
                                />
                                {editBtn}
                            </Box>
                        )
                    }}
                    disabled={disabled}
                />

                <Alert severity={'info'}>
                    {t('Resource assignment note')}
                </Alert>
            </Stack>
        </Property>
    )

    const serviceDetailsFields = (
        <Stack gap={fieldGroupGap}>
            <Property
                title={t('Service Category')}
                tooltip={t('Sets the service grouping and sector; this affects which resources and booking behavior apply.')}
            >
                <Autocomplete<ServiceCategory>
                    id={'categories'}
                    options={categories}
                    getOptionLabel={(option: ServiceCategory) => option.name || ''}
                    value={categories.find(c => c.id === service.categoryIds?.[0]) ?? null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size={'small'}
                            fullWidth
                        />
                    )}
                    fullWidth
                    onChange={(_e, value) => {
                        const categoryIds = value ? [value.id as string] : []
                        const firstSectorId = value?.sectorIds?.[0]
                        serviceData.setItem({
                            ...service,
                            categoryIds,
                            sectorIds: firstSectorId ? [firstSectorId] : []
                        })
                    }}
                    disabled={disabled && !isMewsManaged}
                />
            </Property>

            <Property
                title={t('Market Code (SKU)')}
                tooltip={t('Optional external or reporting code used to identify this service outside Crqlar.')}
            >
                <TextField
                    id={'sku'}
                    size={'small'}
                    value={service.marketCode ?? ''}
                    onChange={(e) => updateField('marketCode', e.target.value)}
                    fullWidth
                    disabled={disabled}
                />
            </Property>

            <Property
                title={t('Age categories')}
                tooltip={t('Guest age categories available when booking or editing reservations for this service.')}
            >
                <Autocomplete
                    id={'age-categories'}
                    multiple
                    options={ageCategories}
                    getOptionLabel={(option: AgeCategory) => option.name || ''}
                    value={service.ageCategoryIds?.map(id => ageCategories.find(c => c.id === id)).filter(Boolean) as AgeCategory[] || []}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size={'small'}
                            fullWidth
                        />
                    )}
                    fullWidth
                    onChange={(_e, value) => updateField('ageCategoryIds', value.map((v: any) => v.id))}
                    disabled={disabled}
                />
            </Property>

            <Property
                title={t('Service Duration (min)')}
                tooltip={t('Default reservation length for this service before resource-specific overrides are applied.')}
            >
                <Stack gap={1} width={'100%'}>
                    <TextField
                        id={'duration'}
                        size={'small'}
                        value={service.noShowService ? '' : (service.durationMinutes ?? '')}
                        onChange={(e) => {
                            const val = parseInt(e.target.value)
                            if (!isNaN(val) && val >= 0) updateField('durationMinutes', val)
                            else if (e.target.value === '') updateField('durationMinutes', undefined)
                        }}
                        fullWidth
                        type={'number'}
                        disabled={disabled || service.noShowService}
                        InputProps={{inputProps: {min: 0}}}
                    />
                    {service.noShowService && (
                        <Alert severity={'warning'}>
                            <Typography variant={'body2'}>{t('Duration cannot be set for no show service!')}</Typography>
                        </Alert>
                    )}
                </Stack>
            </Property>

            {/* A tip service is never booked: its staff are whose tips it takes, in the Tips section */}
            {!service.tipService && linkedResourcesField}
        </Stack>
    )

    // For non-restaurant: Preparation & Clean Up Time & Assignment requirements section
    const preparationSection = (
        // Needed to bring back the service requirements in service details page
        <AdditionalProperties title={t('Preparation & Clean Up Time & Assignment requirements')} variant={'outlined'}>
            {!service.id && (
                <Alert severity={'warning'}>
                    {t('Create the service before adding preparation or resource block times.')}
                </Alert>
            )}

            <Property
                title={t('Preparation Time (min)')}
                tooltip={t('Buffer time before the service starts, optionally different per linked resource category.')}
            >
                <Stack gap={1} width={'100%'}>
                    <Alert severity={"info"}>
                        {t('Preparation time message')}
                    </Alert>

                    {
                        Object.keys(service.preparationTimeMinutes || {}).map(k => {
                            return (
                                <Stack direction={'row'} key={k} width={'100%'} gap={1}>
                                    <Autocomplete
                                        options={linkedResourceCategories}
                                        getOptionLabel={(option: ResourceCategory) => {
                                            const sector = sectors.find(s => option.sectorIds?.includes(s.id as string))
                                            const sectorName = sector?.name
                                            const optionText = option.name

                                            if (sector) {
                                                return `${optionText} - ${sectorName}`
                                            }

                                            return optionText || ''
                                        }}
                                        value={allResourceCategories.find(c => c.id === k)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size={'small'}
                                            />
                                        )}
                                        fullWidth
                                        onChange={(e, value) => {
                                            if (value?.id) {
                                                const newOverrides = {...service.preparationTimeMinutes}
                                                delete newOverrides['']
                                                newOverrides[value.id] = undefined

                                                updateField('preparationTimeMinutes', newOverrides)
                                            }
                                        }}
                                        disabled={disabled || !!allResourceCategories.find(c => c.id === k)}
                                    />

                                    <CountTextField
                                        size={'small'}
                                        value={service.preparationTimeMinutes?.[k] || ''}
                                        onChangeValue={(e) => {
                                            const newOverrides = {
                                                ...service.preparationTimeMinutes,
                                                [k]: e
                                            }
                                            updateField('preparationTimeMinutes', newOverrides)
                                        }}
                                        fullWidth
                                        disabled={disabled}
                                    />

                                    {
                                        !disabled &&
                                        <IconButton
                                            onClick={() => {
                                                const newOverrides = {...service.preparationTimeMinutes}
                                                delete newOverrides[k]
                                                updateField('preparationTimeMinutes', newOverrides)
                                            }}
                                        >
                                            <DeleteIcon sx={{
                                                width: '24px',
                                                height: '24px',
                                                color: CommonThemeColors.sys.error.main
                                            }}/>
                                        </IconButton>
                                    }

                                </Stack>
                            )
                        })
                    }

                    {
                        !disabled && !!service.resourceIds?.length &&
                        <Button
                            disabled={!service.id}
                            onClick={() => {
                                updateField('preparationTimeMinutes', {
                                    ...service.preparationTimeMinutes,
                                    '': ''
                                })
                            }}
                        >
                            {t('Add new preparation time')}
                        </Button>
                    }

                </Stack>
            </Property>

            <Divider/>

            <Property
                title={t('Resource Block (min)')}
                tooltip={t('How long linked resources stay blocked for this service, optionally different per resource category.')}
            >
                <Stack gap={1} width={'100%'}>
                    <Alert severity={"info"}>{t('Resource duration override message')}</Alert>
                    {Object.keys(service.resourceCategoryDurationMinutes || {}).map(k => (
                        <Stack direction={'row'} key={k} width={'100%'} gap={1}>
                            <Autocomplete
                                options={linkedResourceCategories}
                                getOptionLabel={(option: ResourceCategory) => {
                                    const sector = sectors.find(s => option.sectorIds?.includes(s.id as string))
                                    return sector ? `${option.name} - ${sector.name}` : (option.name || '')
                                }}
                                value={allResourceCategories.find(c => c.id === k)}
                                renderInput={(params) => <TextField {...params} fullWidth size={'small'}/>}
                                fullWidth
                                onChange={(e, value) => {
                                    if (value?.id) {
                                        const newOverrides = {...service.resourceCategoryDurationMinutes}
                                        delete newOverrides['']
                                        newOverrides[value.id] = undefined
                                        updateField('resourceCategoryDurationMinutes', newOverrides)
                                    }
                                }}
                                disabled={disabled || !!allResourceCategories.find(c => c.id === k)}
                            />
                            <CountTextField
                                size={'small'}
                                value={service.resourceCategoryDurationMinutes?.[k] || ''}
                                onChangeValue={(e) => {
                                    updateField('resourceCategoryDurationMinutes', {
                                        ...service.resourceCategoryDurationMinutes,
                                        [k]: e
                                    })
                                }}
                                fullWidth
                                disabled={disabled}
                            />
                            {!disabled && (
                                <IconButton onClick={() => {
                                    const newOverrides = {...service.resourceCategoryDurationMinutes}
                                    delete newOverrides[k]
                                    updateField('resourceCategoryDurationMinutes', newOverrides)
                                }}>
                                    <DeleteIcon sx={{width: '24px', height: '24px', color: CommonThemeColors.sys.error.main}}/>
                                </IconButton>
                            )}
                        </Stack>
                    ))}
                    {!disabled && !!service.resourceIds?.length && (
                        <Button
                            disabled={!service.id}
                            onClick={() => {
                                updateField('resourceCategoryDurationMinutes', {
                                    ...service.resourceCategoryDurationMinutes,
                                    '': ''
                                })
                            }}
                        >
                            {t('Add new resource block')}
                        </Button>
                    )}
                </Stack>
            </Property>

            <Divider/>

            <Property
                title={t('Assignment requirements')}
                tooltip={t('Number of resources required from each linked resource category when this service is booked.')}
            >
                <Stack gap={1} width={'100%'}>
                    {Object.keys(service.assignedResourcesRequirementPerCategory || {}).map(k => (
                        <Stack direction={'row'} key={k} width={'100%'} gap={1}>
                            <Autocomplete
                                options={linkedResourceCategories}
                                getOptionLabel={(option: ResourceCategory) => {
                                    const sector = sectors.find(s => option.sectorIds?.includes(s.id as string))
                                    return sector ? `${option.name} - ${sector.name}` : (option.name || '')
                                }}
                                value={allResourceCategories.find(c => c.id === k)}
                                renderInput={(params) => <TextField {...params} fullWidth size={'small'}/>}
                                fullWidth
                                onChange={(e, value) => {
                                    if (value?.id) {
                                        const newAssignments = {...service.assignedResourcesRequirementPerCategory}
                                        delete newAssignments['']
                                        newAssignments[value.id] = 1
                                        updateField('assignedResourcesRequirementPerCategory', newAssignments)
                                    }
                                }}
                                disabled={disabled || !!allResourceCategories.find(c => c.id === k)}
                            />
                            <CountTextField
                                size={'small'}
                                value={service.assignedResourcesRequirementPerCategory?.[k] || ''}
                                onChangeValue={(e) => {
                                    updateField('assignedResourcesRequirementPerCategory', {
                                        ...service.assignedResourcesRequirementPerCategory,
                                        [k]: e
                                    })
                                }}
                                fullWidth
                                disabled={disabled}
                            />
                            {!disabled && (
                                <IconButton onClick={() => {
                                    const newAssignments = {...service.assignedResourcesRequirementPerCategory}
                                    delete newAssignments[k]
                                    updateField('assignedResourcesRequirementPerCategory', newAssignments)
                                }}>
                                    <DeleteIcon sx={{width: '24px', height: '24px', color: CommonThemeColors.sys.error.main}}/>
                                </IconButton>
                            )}
                        </Stack>
                    ))}
                    {!disabled && !!service.resourceIds?.length && (
                        <Button onClick={() => {
                            updateField('assignedResourcesRequirementPerCategory', {
                                ...service.assignedResourcesRequirementPerCategory,
                                '': 1
                            })
                        }}>
                            {t('Add requirement')}
                        </Button>
                    )}
                </Stack>
            </Property>
        </AdditionalProperties>
    )

    const agendasSection = (
        <Property
            title={t("Assigned agendas")}
            tooltip={t('Weekly-planner agendas where this service can be scheduled.')}
        >
            <Autocomplete<AgendaV3, true, false, false>
                id={"agendas"}
                multiple
                options={props.agendas}
                getOptionLabel={(option) => option.name ?? ""}
                value={props.selectedAgendas}
                renderInput={(params) => <TextField {...params} fullWidth
                                                    size={"small"}/>}
                fullWidth
                onChange={(_, value) => props.updateAgendas(value)}
                disabled={disabled || !props.serviceData.item?.id}
            />
        </Property>
    );


    const availabilitySection = (
        <>

            <AvailabilitySection
                service={service}
                updateField={updateField}
                resources={mappedResources}
                availabilities={availabilities}
                exceptions={exceptions}
            />

        </>

    )

    const agendaSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Agenda')}
            </Typography>

            <Stack gap={2}>
                {agendasSection}
                {availabilitySection}
            </Stack>
        </Stack>
    )


    // Content & Translations section aligned with print-template content layout.
    const contentAndTranslationsSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {isRestaurant ? t('Content & Translations (Widget & App)') : t('Content & Translations')}
            </Typography>

            <Stack gap={4}>
                <Stack gap={2}>
                    <Typography variant={'h6'} textAlign={'start'}>
                        {t('Banner image')}
                    </Typography>

                    <ImageUploadField
                        id={'image-picker'}
                        image={service.media}
                        setImage={(image) => {
                            updateField('media', image)
                            if (image) {
                                void serviceData.saveThenSet({...service, media: image})
                            }
                        }}
                        ratio={'landscape'}
                        ratioOptions={ALL_IMAGE_UPLOAD_RATIOS}
                        disabled={disabled}
                    />
                </Stack>

                <Stack gap={2}>
                    <Typography variant={'h6'} textAlign={'start'}>
                        {t('Text content')}
                    </Typography>

                    <Stack gap={1}>
                        <Property
                            title={t('Name translations')}
                            tooltip={t('Localized service names shown in guest-facing content and translated interfaces.')}
                            labelAlignment={'top'}
                        >
                            <LocalizedTextField
                                id={'name-localized'}
                                size={'small'}
                                value={service.nameLocalized}
                                onChange={(e) => updateField('nameLocalized', e)}
                                fullWidth
                                required
                                disabled={disabled}
                            />
                        </Property>

                        <Property
                            title={t('Short description translations')}
                            tooltip={t('Short localized summary used where space is limited, such as booking previews.')}
                            labelAlignment={'top'}
                        >
                            <LocalizedTextField
                                id={'short-description-localized'}
                                size={'small'}
                                value={service.shortDescriptionLocalized}
                                onChange={(e) => updateField('shortDescriptionLocalized', e)}
                                fullWidth
                                required
                                multiline
                                rows={3}
                                disabled={disabled}
                            />
                        </Property>

                        <Property
                            title={t('Description translations')}
                            tooltip={t('Full localized description shown on service detail and booking surfaces.')}
                            labelAlignment={'top'}
                        >
                            <LocalizedTextField
                                id={'description-localized'}
                                size={'small'}
                                value={service.descriptionLocalized}
                                onChange={(e) => updateField('descriptionLocalized', e)}
                                fullWidth
                                required
                                multiline
                                rows={3}
                                disabled={disabled}
                            />
                        </Property>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    )

    // Only on services guests can't book (or that already take tips): whether it is a tip service, and
    // whose tips it takes - the staff of its sectors, linked as its resources
    const tipStaffOptions = sortedResources.filter(resource => (
        !!service.resourceIds?.includes(resource.id!)
        || (isStaff(resource.id!) === true && (!relevantSectorIds.length || sameSectorResourceIds.has(resource.id)))
    ))
    const tipSection = canBeTipService(service) ? (
        <AdditionalProperties key={service.id ?? 'new'} title={t('Tips')} variant={'outlined'} defaultExpanded={!!service.tipService}>
            <Property
                title={t('Tip service')}
                tooltip={t('Confirming a guest\'s signature posts the tip line with a tip service (its market code and VAT): the treating staff member\'s own, else the sector\'s default tip service - the one without staff.')}
            >
                <>
                    <Switch
                        id={'is-tip-service'}
                        checked={!!service.tipService}
                        onChange={(e) => {
                            // A new service has no status yet - shown as Draft, saved as Active: a tip
                            // service starts as one guests can't book
                            if (e.target.checked && !service.status) {
                                serviceData.setItem({
                                    ...service,
                                    tipService: true,
                                    status: hasGuestAppModule ? ServiceStatus.ACTIVE_NOT_BOOKABLE : ServiceStatus.DRAFT
                                })
                            } else {
                                updateField('tipService', e.target.checked)
                            }
                        }}
                        disabled={disabled}
                    />

                    {/* What saving would be refused with: changing it (or turning it off) solves it */}
                    {!!tipServiceWarning && (
                        <Alert severity={'warning'} sx={{mt: 1}}>
                            <Typography variant={'body1'}>{tipServiceWarning}</Typography>
                        </Alert>
                    )}
                </>
            </Property>

            {!!service.tipService && (
                <>
                    <Divider/>
                    <Property
                        title={t('Takes the tips of')}
                        tooltip={t('The staff whose tips this service books on the folio when a signature is confirmed.')}
                        labelAlignment={'top'}
                    >
                        <Stack gap={1} width={'100%'}>
                            <Autocomplete
                                id={'tip-staff'}
                                multiple
                                options={tipStaffOptions}
                                getOptionLabel={option => option.name || ''}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={mappedResources}
                                onChange={(_e, value) => updateField('resourceIds', value.map(resource => resource.id!))}
                                renderInput={(params) => <TextField {...params} fullWidth size={'small'}/>}
                                fullWidth
                                disabled={disabled}
                            />
                            <Alert severity={'info'}>
                                {t('Without staff it\'s the default tip service: it takes the tips of all staff who have no tip service of their own.')}
                            </Alert>
                        </Stack>
                    </Property>
                </>
            )}
        </AdditionalProperties>
    ) : null

    const dataTagsSection = (
        <AdditionalProperties title={t('Advanced Settings')} variant={'outlined'}>
            {!isRestaurant && (
                <>
                    <Property
                        title={t('Is package')}
                        tooltip={t('Marks this service as a package made from child services.')}
                    >
                        <Switch
                            id={'is-package'}
                            checked={service.isPackage}
                            onChange={(e) => updateField('isPackage', e.target.checked)}
                            disabled={disabled}
                        />
                    </Property>

                    {service.isPackage && (
                        <>
                            <Divider/>
                            <Property
                                title={t('Child services')}
                                tooltip={t('Services included when this package is booked.')}
                            >
                                <Autocomplete
                                    id={'services'}
                                    options={services}
                                    multiple
                                    getOptionLabel={(option: any) => option.name || ''}
                                    value={service.packageServiceIds?.map(id => services.find(service => service.id === id)).filter(Boolean) || []}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            size={'small'}
                                        />
                                    )}
                                    fullWidth
                                    onChange={(_e, value) => {
                                        updateField('packageServiceIds', value.map(v => v?.id!))
                                    }}
                                />
                            </Property>
                        </>
                    )}

                    <Divider/>
                </>
            )}

            <Property
                title={t('Is no show service')}
                tooltip={t('Special service used for no-show fees; only one can exist per sector and it has no duration.')}
            >
                <>
                    <Switch
                        id={'is-no-show-service'}
                        checked={service.noShowService}
                        onChange={(e) => {
                            updateField('noShowService', e.target.checked)

                            if (parseInt(e.target.value)) {
                                updateField('durationMinutes', undefined)
                            }
                        }}
                        // If there is already a no show service for one of the selected sectors, we cannot enable this
                        disabled={disabled || noShowAlreadyExistsForSector()}
                    />

                    {noShowAlreadyExistsForSector() && (
                        <Alert severity={'warning'} sx={{mt: 1}}>
                            <Typography variant={'body1'}>
                                {t('A no show service already exists for one of the selected sectors. Only one no show service can exist per sector.')}
                            </Typography>
                        </Alert>
                    )}
                </>
            </Property>

            <Divider/>

            <Property
                title={t('Default service')}
                tooltip={t('Default service used when creating reservations for this sector without another selected service.')}
                labelAlignment={'top'}
            >
                <Stack gap={1.5} width={'100%'} alignItems={'flex-start'}>
                    <Alert severity={'info'} sx={{width: '100%'}}>
                        {t('New reservations created without an explicitly selected service use the sector default. Each sector can have only one default service; setting this service as default replaces the current one.')}
                    </Alert>

                    {!currentSector ? (
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {t('Select and save a service category before setting a default service.')}
                        </Typography>
                    ) : isDefaultService ? (
                        <Typography variant={'body2'}>
                            {t('This is the default service for {{sector}}.', {sector: currentSector.name})}
                        </Typography>
                    ) : (
                        <Stack gap={1} alignItems={'flex-start'}>
                            {currentSector.defaultServiceId ? (
                                <Typography variant={'body2'}>
                                    {t('Current default')}: {' '}
                                    <MuiLink
                                        component={RouterLink}
                                        to={`/system/services/${currentSector.defaultServiceId}`}
                                        target={'_blank'}
                                        rel={'noopener noreferrer'}
                                        underline={'hover'}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}
                                    >
                                        {currentDefaultService?.name || t('View default service')}
                                        <OpenInNewIcon sx={{fontSize: 14}}/>
                                    </MuiLink>
                                </Typography>
                            ) : (
                                <Typography variant={'body2'} color={'text.secondary'}>
                                    {t('No default service is currently set for {{sector}}.', {sector: currentSector.name})}
                                </Typography>
                            )}

                            <LoadingButton
                                variant={'outlined'}
                                size={'small'}
                                loading={defaultServiceMutation.isLoading}
                                onClick={() => defaultServiceMutation.mutate()}
                                disabled={!service.id || serviceData.isDirty}
                            >
                                {t('Set {{service}} as default service', {
                                    service: service.name || t('Service')
                                })}
                            </LoadingButton>

                            {(!service.id || serviceData.isDirty) && (
                                <Typography variant={'caption'} color={'text.secondary'}>
                                    {t('Save this service before changing the sector default.')}
                                </Typography>
                            )}
                        </Stack>
                    )}
                </Stack>
            </Property>

            {hasGuestAppModule && (
                <>
                    <Divider/>

                    <Property
                        title={t('Data tags')}
                        tooltip={t('Custom metadata used by app integrations and guest-app behavior.')}
                    >
                        <Form
                            formData={service?.tags || {}}
                            onChange={(e) => updateField('tags', e.formData)}
                            validator={validator}
                            formContext={{submit: false}}
                            templates={{ButtonTemplates: {SubmitButton: () => <div/>}}}
                            schema={{
                                type: 'object',
                                additionalProperties: [{type: 'string'}],
                                properties: {}
                            } as any}
                            uiSchema={{}}
                        />
                    </Property>
                </>
            )}
        </AdditionalProperties>
    )

    const externalInfoSection = service.externalInfo?.length ? (
        <ExternalInfoPreview
            externalInfo={service.externalInfo}
            setExternalInfo={e => props.updateField?.('externalInfo', e)}
        />
    ) : null

    const serviceDetailsSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Service details')}
            </Typography>

            <Stack gap={2}>
                {serviceDetailsFields}
                {!service.tipService && preparationSection}
                {tipSection}
                {dataTagsSection}
                {showAcademyCourse && (
                    <AcademyCourseSection
                        service={service}
                        services={services}
                        onTagsChange={(tags) => updateField('tags', tags)}
                    />
                )}
                {externalInfoSection}
            </Stack>
        </Stack>
    )

    //endregion UI

    if (props.section === 'content') {
        return (
            <ScreenWrapper>
                <Stack
                    sx={{
                        width: '100%',
                        maxWidth: '900px',
                        gap: {xs: 5, sm: 6},
                        alignSelf: 'center'
                    }}
                >
                    {contentAndTranslationsSection}
                </Stack>
            </ScreenWrapper>
        )
    }

    return (
        <ScreenWrapper>

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    gap: {xs: 5, sm: 6},
                    alignSelf: 'center'
                }}
            >
                {bufferTimeStatusSection}
                {basicInfoSection}
                <Divider sx={sectionDividerSx}/>
                {serviceDetailsSection}
                {isWeeklyPlanner && (
                    <>
                        <Divider sx={sectionDividerSx}/>
                        {agendaSection}
                    </>
                )}

            </Stack>

            {editingResource && (
                <ResourceInlineEditDialog
                    open={true}
                    resource={editingResource}
                    onClose={() => setEditingResource(null)}
                    onSaved={() => setEditingResource(null)}
                />
            )}
        </ScreenWrapper>
    )
}

export default ServiceDetailsForm
